import { Bell, Calendar, History, LayoutGrid, Map, Settings, WifiOff } from 'lucide-react';
import { NavLink, Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDevice, useObsoleto, useStato } from '@/app/DeviceProvider';
import { useMarchio } from '@/app/BrandProvider';
import { useFormato } from '@/app/formato';
import { useAvvisi } from '@/app/useAvvisi';
import { Avvisi, cx } from '@/ui';
import s from './shell.module.css';

function Orologio() {
  const adesso = useStato((st) => st.adesso);
  const f = useFormato();
  return (
    <div className={s.orologio} aria-live="off">
      <span className={s.orologioOra}>{f.ora(adesso)}</span>
      <span className={s.orologioData}>{f.data(adesso)}</span>
    </div>
  );
}

function StatoSistema() {
  const { t } = useTranslation();
  const modalita = useStato((st) => st.stato?.modalita ?? st.impianto?.modalita);
  const { obsoleto } = useObsoleto();
  if (!modalita) return null;
  const attivo = modalita !== 'SPENTO';
  return (
    <div className={s.statoSistema}>
      <span>{t('stato.sistema')}:</span>
      {obsoleto ? (
        <span className={cx(s.statoValore, s.statoValoreErr)}>{t('stato.irraggiungibile')}</span>
      ) : (
        <span className={cx(s.statoValore, attivo && s.statoValoreOk)}>
          {attivo ? t('stato.attivo') : t('stato.spento')} · {t(`modalita.${modalita}`)}
        </span>
      )}
    </div>
  );
}

function Campanello() {
  const { t } = useTranslation();
  const aperti = useStato((st) => st.allarmi.filter((a) => a.stato === 'aperto').length);
  const { obsoleto } = useObsoleto();
  const n = aperti + (obsoleto ? 1 : 0);
  return (
    <NavLink to="/notifiche" className={({ isActive }) => cx(s.campanello, isActive && s.campanelloAttivo)} aria-label={`${t('nav.notifiche')}${n ? ` (${n})` : ''}`}>
      <Bell size={28} aria-hidden />
      {n > 0 && <span className={s.campanelloConteggio}>{n}</span>}
    </NavLink>
  );
}

function BannerConnessione() {
  const { t } = useTranslation();
  const { obsoleto, ultimaLettura } = useObsoleto();
  const pronto = useStato((st) => st.pronto);
  const f = useFormato();
  if (!pronto || !obsoleto) return null;
  return (
    <div className={s.banner} role="alert">
      <WifiOff size={24} aria-hidden />
      <span>
        {t('stato.bannerIrraggiungibile')} {ultimaLettura ? t('stato.ultimaLettura', { ora: f.ora(ultimaLettura) }) : t('stato.nessunaLettura')}
      </span>
    </div>
  );
}

export function AppShell() {
  const { t } = useTranslation();
  const { marchio } = useMarchio();
  const impianto = useStato((st) => st.impianto);
  const avvisi = useAvvisi((a) => a.avvisi);
  useDevice();

  const voci = [
    { to: '/pannello', icona: <LayoutGrid size={26} aria-hidden />, testo: t('nav.pannello') },
    { to: '/zone', icona: <Map size={26} aria-hidden />, testo: t('nav.zone') },
    { to: '/programmazione', icona: <Calendar size={26} aria-hidden />, testo: t('nav.programmazione') },
    { to: '/storico', icona: <History size={26} aria-hidden />, testo: t('nav.storico') },
    { to: '/impostazioni', icona: <Settings size={26} aria-hidden />, testo: t('nav.impostazioni') },
  ];

  return (
    <div className={s.shell}>
      <div>
        <header className={s.testata}>
          <div className={s.marchio}>
            <span className={s.monogramma} aria-hidden>
              {marchio.monogramma}
            </span>
            <div className={s.marchioTesto}>
              <span className={s.marchioNome}>{marchio.nome}</span>
              <span className={s.marchioImpianto}>{impianto ? `${impianto.nome} · ${impianto.indirizzo.citta}` : t('app.caricamento')}</span>
            </div>
          </div>
          <span className={s.spazio} />
          <StatoSistema />
          <Orologio />
          <Campanello />
        </header>
        <BannerConnessione />
      </div>
      <main className={s.contenuto}>
        <Outlet />
      </main>
      <nav className={s.nav} aria-label={t('nav.principale')}>
        {voci.map((v) => (
          <NavLink key={v.to} to={v.to} className={({ isActive }) => cx(s.voce, isActive && s.voceAttiva)}>
            {v.icona}
            <span>{v.testo}</span>
          </NavLink>
        ))}
      </nav>
      <Avvisi avvisi={avvisi} />
    </div>
  );
}
